import { useState } from 'react';
import { NWClient } from '../../client/NWClient';
import { toast } from 'react-toastify';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectUser } from '../../slices/user/userSlice';
import { useTranslation } from 'react-i18next';
import { HandleAnalyzeArgumentsTypes } from './types';
import { Document } from '../../pages/document-vault/components/document-list-vault/types';
import { AIAssistModel } from '../../models/ai.assist.model';
import { setCurrentDocumentAISummary } from '../../slices/current-document-ai-summary/currentDocumentAISummarySlice';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { sanitizeRedirectUrl } from '../helpers/sanitize';

export const useAISummaryDialog = () => {
  const { t } = useTranslation();
  const purposes = [
    {
      title: t('purpose.verification'),
      value: 'verification',
    },
    {
      title: t('purpose.analysis'),
      value: 'analysis',
    },
    {
      title: t('purpose.recordKeeping'),
      value: 'record keeping',
    },
  ];
  const currentUser = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const generateSummaryParam = params.get('generateSummary');
  const backURL = params.get('backURL');
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AIAssistModel>(null);
  const [documentType, setDocumentType] = useState<string>('contract');
  const [purpose, setPurpose] = useState(purposes);
  const [resultFormat, setResultFormat] = useState<string>('table');
  const [detailLevel, setDetailLevel] = useState<string>('high level');
  const [simplifyJargon, setSimplifyJargon] = useState<boolean>(true);
  const [contextualInformation, setContextualInformation] = useState('');
  const [analysisLoading, setAnalysisLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [document, setDocument] = useState<Document>(null);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setAnalysisLoading(false);
    setDocumentType('contract');
    setPurpose(purposes);
    setResultFormat('table');
    setDetailLevel('high level');
    setSimplifyJargon(true);
    setContextualInformation('');
    setDocument(null);
  };

  const handleExtrasExtraction = (isDocumentContract: any, documentId: any) => {
    NWClient.post('document-extras-extraction', {
      [isDocumentContract ? 'document_contract_id' : 'document_upload_id']:
        typeof documentId === 'string' ? Number(documentId) : documentId,
    });
  };

  const handleAnalyze = ({
    handleAnalyzeSuccess,
    handleAnalyzeError,
    isDocumentContract,
    documentId,
  }: HandleAnalyzeArgumentsTypes) => {
    setAnalysisLoading(true);
    NWClient.post(
      'ai-assist',
      {
        [isDocumentContract ? 'document_contract_id' : 'document_upload_id']:
          typeof documentId === 'string' ? Number(documentId) : documentId,
        document_type: documentType,
        result_format: resultFormat,
        detail_level: detailLevel,
        purpose: JSON.stringify(purpose.map((el) => el.value)),
        simplify_jargon: simplifyJargon,
        contextual_information: contextualInformation,
        user: currentUser.id,
      },
      true
    )
      .then((res: AIAssistModel) => {
        if (Array.isArray(res.content)) {
          throw new Error();
        }
        setSummary(res);
        localStorage.setItem('AISummaryFail', 'false');
        dispatch(setCurrentDocumentAISummary(res));
        handleAnalyzeSuccess && handleAnalyzeSuccess();
      })
      .then(() => {
        if (generateSummaryParam) {
          const safeBackURL = backURL ? sanitizeRedirectUrl(backURL) : '';
          isDocumentContract
            ? navigate(
                window.location.pathname +
                  '?contractGenerated=true' +
                  (safeBackURL ? '&backURL=' + encodeURIComponent(safeBackURL) : '') +
                  '&initialTab=1'
              )
            : navigate(
                window.location.pathname +
                  '?initialTab=1' +
                  (safeBackURL ? '&backURL=' + encodeURIComponent(safeBackURL) : '')
              );
        }
      })
      .catch((error) => {
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0].includes(t('messages.exceededPlanLimits'))
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
          handleCloseDialog();
        } else {
          if (
            error.message === 'Request failed with status code 401' ||
            error.message === 'Request failed with status code 403'
          ) {
            toast.error(t('messages.noPermissionsMessage'));
          } else if (localStorage.getItem('AISummaryFail') === 'true') {
            toast.error(error.message ? error.message : t('messages.errorOccurred'), {
              theme: 'colored',
            });
          } else {
            localStorage.setItem('AISummaryFail', 'true');
            toast.error(t('messages.firstAISummaryFail'));
          }
        }
        handleAnalyzeError && handleAnalyzeError();
      })
      .finally(() => {
        handleCloseDialog();
        handleExtrasExtraction(isDocumentContract, documentId);
      });
  };

  return {
    handleAnalyze,
    analysisLoading,
    setAnalysisLoading,
    dialogOpen,
    setDialogOpen,
    handleCloseDialog,
    documentType,
    setDocumentType,
    purpose,
    setPurpose,
    resultFormat,
    setResultFormat,
    detailLevel,
    setDetailLevel,
    simplifyJargon,
    setSimplifyJargon,
    contextualInformation,
    setContextualInformation,
    document,
    setDocument,
    purposes,
    summary,
  };
};
